/**
 * Supabase auth storage adapter — cross-subdomain SSO via shared cookies.
 *
 * Day 62a (ADR 38). Replaces the default localStorage backing for Supabase
 * JS auth so a session signed in on arbi.ironcladsupply.co also resolves
 * on arbi-dev.ironcladsupply.co (and any future *.ironcladsupply.co
 * subdomain).
 *
 * Runtime detection:
 *   • `window.location.hostname.endsWith('.ironcladsupply.co')` → cookie
 *     storage scoped to `domain=.ironcladsupply.co` (cross-subdomain).
 *   • Otherwise (localhost, Vercel preview URLs like
 *     arbi-isg-xxx.vercel.app) → localStorage fallback. Preview deploys
 *     are intentionally session-isolated from prod.
 *
 * Cookie attributes: `Secure; SameSite=Lax; Path=/; Domain=.ironcladsupply.co`.
 * No HttpOnly — the JS client must read the token. Max-Age = 1 year;
 * Supabase manages refresh-token rotation independently.
 *
 * Size ceiling: cookie values capped at ~3KB encoded (browsers cap each
 * cookie at ~4KB total). Current Supabase auth tokens are 1–2KB, so the
 * ceiling is comfortable. If a future token exceeds the ceiling, we log
 * once and fall back to localStorage for that key — preserves correctness
 * over SSO when the value can't physically fit.
 *
 * Migration on first setItem after SSO lands: any pre-existing localStorage
 * entry is cleared once the cookie is written, so `getItem` doesn't return
 * a stale localStorage value on the next call.
 *
 * Test surface: ADR 38 verification gate at end of Day 62a includes a
 * Safari ITP smoke (sign in on arbi.ironcladsupply.co, navigate to
 * arbi-dev.ironcladsupply.co in same window, confirm session persists).
 * If Safari fails, fallback procedure: revert src/lib/supabase.ts to
 * default storage, log BACKLOG follow-up, ship Day 62a with double-login.
 */

interface ArbiStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const COOKIE_DOMAIN = '.ironcladsupply.co';
const COOKIE_VALUE_CEILING = 3000;
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function isProductionSubdomain(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.endsWith('.ironcladsupply.co');
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const target = encodeURIComponent(name) + '=';
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      try {
        return decodeURIComponent(trimmed.slice(target.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string): boolean {
  if (typeof document === 'undefined') return false;
  const encoded = encodeURIComponent(value);
  if (encoded.length > COOKIE_VALUE_CEILING) {
    console.warn(
      '[arbi] Supabase auth value exceeds cookie ceiling (' +
        encoded.length +
        ' > ' +
        COOKIE_VALUE_CEILING +
        '). Falling back to localStorage for key: ' +
        name
    );
    return false;
  }
  const attrs = [
    'Path=/',
    'Domain=' + COOKIE_DOMAIN,
    'SameSite=Lax',
    'Secure',
    'Max-Age=' + COOKIE_MAX_AGE_SECONDS,
  ].join('; ');
  document.cookie = encodeURIComponent(name) + '=' + encoded + '; ' + attrs;
  return true;
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const attrs = ['Path=/', 'Domain=' + COOKIE_DOMAIN, 'SameSite=Lax', 'Secure', 'Max-Age=0'].join(
    '; '
  );
  document.cookie = encodeURIComponent(name) + '=; ' + attrs;
}

export function createSupabaseStorage(): ArbiStorage {
  return {
    getItem(key: string): string | null {
      if (isProductionSubdomain()) {
        const cookieVal = readCookie(key);
        if (cookieVal !== null) return cookieVal;
      }
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key: string, value: string): void {
      if (isProductionSubdomain()) {
        const wrote = writeCookie(key, value);
        if (wrote) {
          try {
            window.localStorage.removeItem(key);
          } catch {
            /* ignore */
          }
          return;
        }
      }
      try {
        window.localStorage.setItem(key, value);
      } catch {
        /* ignore */
      }
    },
    removeItem(key: string): void {
      if (isProductionSubdomain()) {
        deleteCookie(key);
      }
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}
